import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import { useAuthStore } from '@wordpuppi/shared';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ArrowLeft, LogOut } from 'lucide-react';
import logoBlack from '@/assets/logo-black-300w.webp';
import logoWhite from '@/assets/logo-white-300w.webp';

// Matches the site sidebar's active-item treatment (raised surface, amber dot),
// so the two navs read as one design language.
export const settingsItemClass = (active: boolean) =>
  cn(
    'flex items-center gap-2 px-3 py-2 text-sm text-sidebar-muted rounded-md outline-none transition-colors whitespace-nowrap hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active && 'bg-sidebar-accent text-sidebar-foreground font-medium',
  );

/**
 * Chrome shared by the settings sections (AB#594 /account, and /user).
 * `children` are the nav links; Log out is appended, and the section's child
 * route renders in the main column. Callers keep their own <Link> list so the
 * router's literal path types survive.
 */
export function SettingsShell({
  navLabel,
  children,
}: {
  navLabel: string;
  children: ReactNode;
}) {
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const redirected = useRef(false);

  useEffect(() => {
    if (!isAuthenticated && !redirected.current) {
      redirected.current = true;
      navigate({ to: '/login' });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-card text-card-foreground py-3 px-4 sm:px-8 border-b border-border">
        <div className="max-w-5xl mx-auto flex justify-between items-center gap-3">
          <Link
            to="/sites"
            className="no-underline block rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <img src={logoBlack} alt="WordPuppi" className="dark:hidden" style={{ height: 'calc(var(--spacing) * 14)' }} />
            <img src={logoWhite} alt="WordPuppi" className="hidden dark:block" style={{ height: 'calc(var(--spacing) * 14)' }} />
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/sites"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors no-underline outline-none rounded-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <ArrowLeft className="h-4 w-4" />
              Sites
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-5xl mx-auto p-4 sm:p-8 flex flex-col md:flex-row gap-6 md:gap-8">
        {/* Side menu: horizontal scroll row on mobile, vertical rail on md+ */}
        <nav
          aria-label={navLabel}
          className="md:w-52 shrink-0 flex md:flex-col gap-1 overflow-x-auto border-b md:border-b-0 md:border-r border-border pb-2 md:pb-0 md:pr-2 -mx-1 px-1"
        >
          {children}

          {/* AB#844: sign out from the settings nav. Same store action the header
              menu and site sidebar use; the !isAuthenticated effect above does
              the redirect to /login. */}
          <button
            type="button"
            data-testid="account-logout"
            onClick={() => logout()}
            className={cn(
              settingsItemClass(false),
              'md:mt-2 md:border-t md:border-border md:pt-3 md:rounded-none',
            )}
          >
            <LogOut className="size-4 shrink-0" />
            Log out
          </button>
        </nav>

        <main className="flex-1 min-w-0 space-y-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
